import { createAsyncThunk } from '@reduxjs/toolkit'
import {
  getPurchaseOrderDetailApi,
  uploadFileGrnOrDeliveryNoteApi,
  submitDocumentApi,
  getDocumentPoDetailApi,
  deletePoDetailDocument,
  getDownloadPoDocument,
  PurchaseOrderDocumentDataType,
} from 'features/PurchaseOrder/services/purchaseOrder'
import { toastSuccess, toastFailed } from 'utils/toast'
import { downloadFileBlob } from 'utils/downloadFileUrl'
import { callErrorMsg } from 'helpers/errorMsg'

export const SLICE_NAME_PURCHASE_ORDER_DETAIL = 'purchaseOrderDetail'

export const fetchPurchaseOrderDetail = createAsyncThunk(
  `${SLICE_NAME_PURCHASE_ORDER_DETAIL}/fetchPurchaseOrderDetail`,
  async (purchaseOrderId: number, { rejectWithValue }) => {
    try {
      const response = await getPurchaseOrderDetailApi(purchaseOrderId)
      return response.data
    } catch (error) {
      return rejectWithValue(error)
    }
  },
)

export interface UploadGrnOrDeliveryNoteDataArgsInterface {
  grnFiles: File[]
  deliveryNoteFiles: File[]
  callbackFunction?: () => void
}

const uploadFiles = (files: File[]) =>
  Promise.all(
    files.map((file) => {
      const formData = new FormData()
      formData.append('file_input', file)
      return uploadFileGrnOrDeliveryNoteApi(formData)
    }),
  )

export const uploadGrnOrDeliveryNoteData = createAsyncThunk(
  `${SLICE_NAME_PURCHASE_ORDER_DETAIL}/uploadGrnOrDeliveryNoteData`,
  async (
    { grnFiles, deliveryNoteFiles, callbackFunction }: UploadGrnOrDeliveryNoteDataArgsInterface,
    { rejectWithValue },
  ) => {
    try {
      const [grnResponses, deliveryNoteResponses] = await Promise.all([
        uploadFiles(grnFiles),
        uploadFiles(deliveryNoteFiles),
      ])

      toastSuccess('Dokumen berhasil diupload')

      if (callbackFunction) callbackFunction()

      return {
        grn: grnResponses.map((response) => ({
          fileUrl: response.data.data.fileUrl,
          isNew: true as const,
        })),
        deliveryNotes: deliveryNoteResponses.map((response) => ({
          fileUrl: response.data.data.fileUrl,
          isNew: true as const,
        })),
      }
    } catch (error) {
      callErrorMsg(error, 'Dokumen gagal diupload')
      return rejectWithValue(error)
    }
  },
)

export interface SubmitDocumentGrnOrDeliveryNoteDataArgsInterface {
  purchaseOrderId: number
  callbackFunction?: () => void
}

export const submitDocumentGrnOrDeliveryNoteData = createAsyncThunk(
  `${SLICE_NAME_PURCHASE_ORDER_DETAIL}/submitDocumentGrnOrDeliveryNoteData`,
  async (
    { purchaseOrderId, callbackFunction }: SubmitDocumentGrnOrDeliveryNoteDataArgsInterface,
    { rejectWithValue, getState },
  ) => {
    try {
      const { uploadedDocuments } = (getState() as StoreStateType).purchaseOrderDetail

      await submitDocumentApi(purchaseOrderId, {
        documents: [
          ...uploadedDocuments.grn
            .filter((doc) => doc.isNew)
            .map((doc) => ({ type: 'GRN' as const, file_url: doc.fileUrl })),
          ...uploadedDocuments.deliveryNotes
            .filter((doc) => doc.isNew)
            .map((doc) => ({ type: 'SURAT_JALAN' as const, file_url: doc.fileUrl })),
        ],
      })

      if (callbackFunction) callbackFunction()
      return undefined
    } catch (error) {
      return rejectWithValue(error)
    }
  },
)

export interface GetPoDocumentsDataArgsInterface {
  purchaseOrderId: number
  callbackFunction?: (documents: PurchaseOrderDocumentDataType[]) => void
}

export interface DeletePoDocumentByIdArgsInterface {
  purchaseOrderId: number
  documentId: number
  callbackFunction?: () => void
}

export const deletePoDocumentById = createAsyncThunk(
  `${SLICE_NAME_PURCHASE_ORDER_DETAIL}/deletePoDocumentById`,
  async (
    { purchaseOrderId, documentId, callbackFunction }: DeletePoDocumentByIdArgsInterface,
    { rejectWithValue },
  ) => {
    try {
      await deletePoDetailDocument(purchaseOrderId, documentId)
      if (callbackFunction) callbackFunction()
      return undefined
    } catch (error) {
      return rejectWithValue(error)
    }
  },
)

export const getPoDocumentsData = createAsyncThunk(
  `${SLICE_NAME_PURCHASE_ORDER_DETAIL}/getPoDocumentsData`,
  async (
    { purchaseOrderId, callbackFunction }: GetPoDocumentsDataArgsInterface,
    { rejectWithValue },
  ) => {
    try {
      const response = await getDocumentPoDetailApi(purchaseOrderId)
      if (callbackFunction) callbackFunction(response.data.documents)
      return response.data.documents
    } catch (error) {
      return rejectWithValue(error)
    }
  },
)

export interface DownloadPoDocumentArgsInterface {
  fileUrl: string
}

export const downloadDocumentGrnOrDeliveryNote = createAsyncThunk(
  `${SLICE_NAME_PURCHASE_ORDER_DETAIL}/downloadDocumentGrnOrDeliveryNote`,
  async ({ fileUrl }: DownloadPoDocumentArgsInterface, { rejectWithValue }) => {
    try {
      const response = await getDownloadPoDocument(fileUrl)
      const fileName = decodeURIComponent(fileUrl.split('/').pop() ?? 'document')
      downloadFileBlob({ data: response.data, fileName, mimeType: 'application/*' })
      return undefined
    } catch (error) {
      toastFailed('Gagal mengunduh dokumen')
      return rejectWithValue(error)
    }
  },
)
